import React from 'react'
import { Clock, RefreshCw, AlertTriangle } from 'lucide-react'

interface SessionTimerDisplayProps {
  remainingTime: number
  formattedTime: string
  isWarning: boolean
  onExtendSession: () => void
  className?: string
  isMobile?: boolean
}

const SessionTimerDisplay: React.FC<SessionTimerDisplayProps> = ({
  remainingTime,
  formattedTime,
  isWarning,
  onExtendSession,
  className = '',
  isMobile = false,
}) => {
  // 남은 시간이 충분하면 타이머 표시 안 함 (세션 타이머 비활성화 시)
  if (remainingTime > 60 * 60) {
    return null
  }

  // 모바일에서는 경고 상태일 때만 표시하여 공간 절약
  if (isMobile && remainingTime > 5 * 60) {
    return null
  }

  const getTimerColor = () => {
    if (remainingTime <= 2 * 60) {
      return 'text-red-600 dark:text-red-400'
    } // Last 2 minutes - red
    if (remainingTime <= 5 * 60) {
      return 'text-amber-600 dark:text-amber-400'
    } // Last 5 minutes - amber
    return 'text-gray-600 dark:text-gray-400' // Default
  }

  const getBackgroundColor = () => {
    if (remainingTime <= 2 * 60) {
      return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
    }
    if (remainingTime <= 5 * 60) {
      return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
    }
    return 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
  }

  const formatMobileTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60

    if (minutes > 0) {
      return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
    } else {
      return `0:${remainingSeconds.toString().padStart(2, '0')}`
    }
  }

  return (
    <div
      className={`inline-flex items-center ${isMobile ? 'space-x-1 px-2 py-1' : 'space-x-2 px-3 py-1.5'} rounded-lg border ${getBackgroundColor()} ${className}`}
    >
      {/* Warning icon for critical time */}
      {remainingTime <= 3 * 60 && (
        <AlertTriangle
          className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} animate-pulse text-red-500`}
        />
      )}

      {/* Clock icon - 모바일에서는 경고 상태가 아닐 때만 표시 */}
      {(!isMobile || remainingTime > 3 * 60) && (
        <Clock
          className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} ${getTimerColor()}`}
        />
      )}

      {/* Time display */}
      <span
        className={`${isMobile ? 'text-xs' : 'text-sm'} font-medium ${getTimerColor()}`}
      >
        {isMobile ? formatMobileTime(remainingTime) : formattedTime}
      </span>

      {/* Extend session button - show when warning */}
      {isWarning && (
        <button
          onClick={onExtendSession}
          className={`${isMobile ? 'ml-1' : 'ml-2'} inline-flex items-center ${isMobile ? 'px-1 py-0.5' : 'px-2 py-1'} rounded bg-blue-100 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50`}
          title="세션 연장"
        >
          <RefreshCw
            className={`${isMobile ? 'h-2.5 w-2.5' : 'h-3 w-3'} ${isMobile ? '' : 'mr-1'}`}
          />
          {!isMobile && '연장'}
        </button>
      )}
    </div>
  )
}

export default SessionTimerDisplay
